import { defineStore } from "pinia";
import { ref } from "vue";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@composables/handleError.js";
import { captureEvent } from "@/lib/analytics";

export const useTemplatesStore = defineStore("templates", () => {
  // State
  const templates = ref([]);
  const templateOptions = ref([]);
  const templatesCount = ref(0);
  const templatesSearchQuery = ref("");
  const activeFilter = ref([]);
  const accountsFilter = ref([]);
  const currentPage = ref(1);

  // Actions - Network
  const getTemplates = async () => {
    try {
      const searchParams = new URLSearchParams();
      if (currentPage.value != null) searchParams.append("page", currentPage.value);
      if (templatesSearchQuery.value) searchParams.append("name", templatesSearchQuery.value);
      if (Array.isArray(activeFilter.value)) activeFilter.value.forEach((v) => searchParams.append("active[]", v));
      if (Array.isArray(accountsFilter.value))
        accountsFilter.value.forEach((v) => searchParams.append("accounts[]", v));

      const url = `/templates?${searchParams.toString()}`;
      const { response, data } = await useMyFetch(url).json().get();

      if (response.value.ok) {
        const { templates: templatesData, templates_count: templatesCountData } = data.value;
        templates.value = templatesData;
        templatesCount.value = templatesCountData;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const getTemplateOptions = async (filters = {}) => {
    try {
      const searchParams = new URLSearchParams();
      if (Array.isArray(filters.active)) filters.active.forEach((v) => searchParams.append("active[]", v));
      if (Array.isArray(filters.accounts)) filters.accounts.forEach((v) => searchParams.append("accounts[]", v));
      if (Array.isArray(filters.base)) filters.base.forEach((v) => searchParams.append("base[]", v));
      if (filters.perPage !== undefined) searchParams.append("per_page", filters.perPage);

      const url = `/templates?${searchParams.toString()}`;
      const { response, data } = await useMyFetch(url).json().get();

      if (response.value.ok)
        templateOptions.value = data.value.templates.map((template) => ({
          value: template.id,
          label: template.name,
        }));
    } catch (err) {
      handleError(err);
    }
  };

  const removeTemplate = async (templateToken) => {
    try {
      const { response } = await useMyFetch(`/templates/${templateToken}`).delete().json();
      if (response.value.ok) {
        const listIndex = templates.value.findIndex((c) => c.token === templateToken);
        if (listIndex !== -1) templates.value.splice(listIndex, 1);

        captureEvent("email_template_deleted", { template_token: templateToken });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const updateTemplate = async (templateToken, templateParams) => {
    try {
      const { response, data } = await useMyFetch(`/templates/${templateToken}`).patch(templateParams).json();
      if (!response.value.ok) return;

      captureEvent("email_template_updated", { template_token: templateToken });

      const index = templates.value.findIndex((c) => c.token === templateToken);
      if (index !== -1)
        templates.value[index] = {
          ...templates.value[index],
          ...templateParams,
        };

      return { response, data };
    } catch (err) {
      handleError(err);
    }
  };

  const deactivateTemplate = (templateToken) => updateTemplate(templateToken, { active: false });
  const activateTemplate = (templateToken) => updateTemplate(templateToken, { active: true });
  const setAsBaseTemplate = (templateToken) => updateTemplate(templateToken, { base: true });
  const unsetAsBaseTemplate = (templateToken) => updateTemplate(templateToken, { base: false });

  const duplicateTemplate = async (templateToken) => {
    try {
      const { response, data } = await useMyFetch(`/templates/${templateToken}/duplicate`).post().json();
      if (response.value.ok) {
        templates.value.splice(0, 0, data.value);

        captureEvent("email_template_duplicated", { template_token: templateToken });
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Actions - Local mutations
  const changePage = async (newPage) => {
    currentPage.value = newPage;
    await getTemplates();
  };

  const clearFilters = async () => {
    resetAllFilters(true);
    await getTemplates();
  };

  const resetAllFilters = (exceptPageAndName = false) => {
    activeFilter.value = [];
    accountsFilter.value = [];
    if (!exceptPageAndName) {
      resetSearchQuery();
      currentPage.value = 1;
    }
  };

  const { resetSearchQuery } = useDebouncedSearchQuery(templatesSearchQuery, () => getTemplates());

  return {
    // state
    templates,
    templateOptions,
    templatesCount,
    templatesSearchQuery,
    currentPage,
    activeFilter,
    accountsFilter,

    // network actions
    getTemplates,
    getTemplateOptions,
    removeTemplate,
    duplicateTemplate,
    activateTemplate,
    deactivateTemplate,
    setAsBaseTemplate,
    unsetAsBaseTemplate,

    // local actions
    changePage,
    clearFilters,
    resetAllFilters,

    // getters & filter refs
  };
});
